// processDateFields — the AddEventForm submit payload builder.
//
// Extracted VERBATIM from `components/AddEventForm.js` (the `processDateFields`
// helper) as part of the behavior-preserving refactor (issue #240). This IS the
// payload builder: `handleSave` calls `processDateFields(formData, eventType)`
// and passes the result straight to `onAddEvent`, with no further assembly. It
// returns a shallow copy of the form data with the per-type date/time text
// fields converted to epoch milliseconds, while every non-date field passes
// through unchanged:
//   - flight : departureDate+departureTime → departureTime (epoch);
//              arrivalDate+arrivalTime → arrivalTime (epoch)
//   - hotel  : checkInDate → epoch; checkOutDate → epoch
//   - car    : pickupDate → epoch; returnDate → epoch
//   - dining : reservationDate+reservationTime → reservationTime (epoch)
//
// Does NOT read the clock: each `new Date(...)` parses the user-entered absolute
// date/time string (e.g. `2025-06-15T10:00`), not the current time, so NO `now`
// injection is required — matching the source exactly.
//
// Totality mirrors the source EXACTLY:
//   - The conversions are guarded by the SAME truthiness checks the source used
//     (e.g. `if (processed.departureDate && processed.departureTime)`), so blank
//     fields are left untouched.
//   - An unrecognized type hits the `default` branch and the data passes through
//     unchanged, just as in the source.
//   - `new Date(...).getTime()` on an unparseable string yields `NaN` here too;
//     the source added no guard against that, so none is added here.
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

/**
 * Convert the date/time form fields into epoch timestamps based on event type.
 *
 * @param {Object} data - The current form values.
 * @param {string} type - The active event type.
 * @returns {Object} A shallow copy of `data` with per-type date/time fields
 *                   converted to epoch milliseconds.
 */
export function processDateFields(data, type) {
  const processed = { ...data };
  switch (type) {
    case 'flight':
      if (processed.departureDate && processed.departureTime) {
        processed.departureTime = new Date(`${processed.departureDate}T${processed.departureTime}`).getTime();
      }
      if (processed.arrivalDate && processed.arrivalTime) {
        processed.arrivalTime = new Date(`${processed.arrivalDate}T${processed.arrivalTime}`).getTime();
      }
      break;
    case 'hotel':
      if (processed.checkInDate) {
        processed.checkInDate = new Date(processed.checkInDate).getTime();
      }
      if (processed.checkOutDate) {
        processed.checkOutDate = new Date(processed.checkOutDate).getTime();
      }
      break;
    case 'car':
      if (processed.pickupDate) {
        processed.pickupDate = new Date(processed.pickupDate).getTime();
      }
      if (processed.returnDate) {
        processed.returnDate = new Date(processed.returnDate).getTime();
      }
      break;
    case 'dining':
      if (processed.reservationDate && processed.reservationTime) {
        processed.reservationTime = new Date(`${processed.reservationDate}T${processed.reservationTime}`).getTime();
      }
      break;
    default:
      break;
  }
  return processed;
}

export default processDateFields;
